import { useCallback, useMemo, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import Svg, { Circle, G, Line, Path, Text as SvgText } from "react-native-svg";
import { useUnistyles } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { isWeb } from "@src/utils/web-helpers";
import type { DailyActivitySeries } from "../dailyActivity";

interface DailyActivityTrendProps {
  series: DailyActivitySeries;
  /** Optional override height; the SVG keeps its 760x170 viewBox aspect. */
  height?: number;
}

const VIEW_W = 760;
const VIEW_H = 170;
/**
 * Left padding holds the Y-axis tick labels. The widest expected
 * label is a 4-digit count like "9,999"; 44px gives that room
 * while keeping the actual plot area generous on narrow viewports.
 */
const PAD_X_LEFT = 44;
const PAD_X_RIGHT = 12;
const PAD_Y_TOP = 16;
const PAD_Y_BOTTOM = 28;
const GRID_LINES = 5;
/**
 * Gap between the right edge of a Y-axis label and the start of
 * the plot area. Keeps tick labels visually anchored to their
 * grid line without crowding the leftmost data point.
 */
const Y_LABEL_RIGHT_GAP = 6;
/**
 * Y-coordinate of the X-axis label row (Mon, Tue, …). Sits below
 * the bottom gridline so the data baseline reads as `0` on the
 * Y-axis without the categorical labels visually intruding into
 * the plot area.
 */
const X_AXIS_LABEL_Y = VIEW_H - 6;

/** Tooltip dimensions used when nudging it inside the chart. */
const TOOLTIP_WIDTH = 184;
const TOOLTIP_HEIGHT = 56;
/** Horizontal gap between the active point and the tooltip's anchor edge. */
const TOOLTIP_GAP = 12;

/**
 * Two-line trend chart (people + vehicles) for a single site's week.
 *
 * Mirrors the static report's SVG: straight polyline through one
 * point per weekday, plus a marker per data point. Implemented with
 * `react-native-svg` so the same code paints on web and native.
 *
 * Polylines (rather than smoothed cubics) so a single weekday spike
 * can't pull the curve below 0 or above `vmax` — the line's extrema
 * are guaranteed to sit at sample points.
 *
 * On web the chart is interactive: hovering or scrubbing across the
 * plot snaps to the nearest weekday, highlights both series at that
 * column with a vertical guide, and shows a tooltip with the day's
 * People / Vehicles values. Native users see the static rendering
 * (no pointer events fire there).
 */
export function DailyActivityTrend({ series, height }: DailyActivityTrendProps) {
  const { theme } = useUnistyles();

  const peopleColor = theme.colors.grey[400];
  const vehicleColor = theme.colors.blue[500];
  const gridColor = theme.colors.borderCard;
  const labelColor = theme.colors.textMuted;
  const guideColor = theme.colors.borderCard;
  const tooltipBg = theme.colors.surface;
  const tooltipBorder = theme.colors.borderCard;

  const { peoplePath, vehiclesPath, peoplePoints, vehiclesPoints, gridYs, gridValues, xs } = useMemo(
    () => buildPlot(series),
    [series],
  );

  const [containerWidth, setContainerWidth] = useState(0);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  /**
   * Map a container-relative X (rendered pixels) to the nearest data
   * point index. The SVG uses `preserveAspectRatio="xMidYMid meet"`,
   * so its content scales uniformly to the container width — the X
   * axis maps linearly with that scale factor.
   */
  const indexFromContainerX = useCallback(
    (containerX: number): number | null => {
      if (containerWidth <= 0 || xs.length === 0) return null;
      const scale = VIEW_W / containerWidth;
      const svgX = containerX * scale;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < xs.length; i += 1) {
        const dist = Math.abs(svgX - (xs[i] ?? 0));
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    },
    [containerWidth, xs],
  );

  const handlePointer = useCallback(
    (event: { nativeEvent: { offsetX?: number; locationX?: number } }) => {
      const offsetX = event.nativeEvent.offsetX ?? event.nativeEvent.locationX ?? 0;
      const next = indexFromContainerX(offsetX);
      setHoverIndex(next);
    },
    [indexFromContainerX],
  );

  const clearHover = useCallback(() => setHoverIndex(null), []);

  // RN-web forwards mouse events on `View`; types live in the Flow defs.
  // Touch events reuse `locationX` from RN's gesture system on native.
  const interactionProps =
    isWeb ?
      ({
        onMouseMove: handlePointer,
        onMouseLeave: clearHover,
      } as object)
    : {};

  const activePeople = hoverIndex !== null ? peoplePoints[hoverIndex] : null;
  const activeVehicles = hoverIndex !== null ? vehiclesPoints[hoverIndex] : null;
  const activeX = hoverIndex !== null ? (xs[hoverIndex] ?? 0) : 0;

  const tooltip =
    hoverIndex !== null && containerWidth > 0 ?
      buildTooltipPlacement({
        svgX: activeX,
        containerWidth,
      })
    : null;

  return (
    <View gap="sm">
      <Text textStyle="xs/semibold" color="textMuted" letterSpacing={0.5} textTransform="uppercase">
        Daily activity trend
      </Text>
      <View
        onLayout={handleLayout}
        style={{ position: "relative", width: "100%" }}
        onTouchStart={isWeb ? undefined : handlePointer}
        onTouchMove={isWeb ? undefined : handlePointer}
        onTouchEnd={isWeb ? undefined : clearHover}
        {...interactionProps}
      >
        <Svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          width="100%"
          height={height ?? 170}
          preserveAspectRatio="xMidYMid meet"
        >
          <G>
            {gridYs.map((y, i) => (
              <Line
                key={`grid-${i}`}
                x1={PAD_X_LEFT}
                y1={y}
                x2={VIEW_W - PAD_X_RIGHT}
                y2={y}
                stroke={gridColor}
                strokeWidth={1}
                strokeDasharray="3 4"
              />
            ))}
            {gridYs.map((y, i) => {
              /*
               * Tick visibility rule:
               *  - Always render the topmost gridline (= `vmax`) and
               *    the bottommost gridline (= `0`). These mark the
               *    actual data range so they belong on the chart.
               *  - For intermediate gridlines, render only when the
               *    value is distinct from `vmax`, distinct from `0`,
               *    *and* distinct from the previously rendered value.
               *
               * With small `vmax` (e.g. `vmax=1`) the integer-rounded
               * gridValues collapse — `vmax=1` produces `[1,1,1,0,0]`
               * — and this rule drops the redundant ticks while
               * keeping the `1` anchored to the top gridline and the
               * `0` anchored to the bottom gridline (where the data
               * baseline actually sits). For larger `vmax` it just
               * removes accidental adjacent duplicates.
               */
              const value = gridValues[i] ?? 0;
              const isTop = i === 0;
              const isBottom = i === gridValues.length - 1;
              if (!isTop && !isBottom) {
                const top = gridValues[0] ?? 0;
                const bottom = gridValues[gridValues.length - 1] ?? 0;
                if (value === top || value === bottom) return null;
                const prev = gridValues[i - 1] ?? null;
                if (prev !== null && prev === value) return null;
              }
              return (
                <SvgText
                  key={`y-label-${i}`}
                  // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
                  x={PAD_X_LEFT - Y_LABEL_RIGHT_GAP}
                  // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
                  y={y + 3}
                  fontSize={10}
                  textAnchor="end"
                  fill={labelColor}
                >
                  {value.toLocaleString()}
                </SvgText>
              );
            })}
          </G>

          {hoverIndex !== null ?
            <Line
              x1={activeX}
              y1={PAD_Y_TOP}
              x2={activeX}
              y2={VIEW_H - PAD_Y_BOTTOM}
              stroke={guideColor}
              strokeWidth={1}
            />
          : null}

          <Path d={peoplePath} fill="none" stroke={peopleColor} strokeWidth={2.6} />
          <Path d={vehiclesPath} fill="none" stroke={vehicleColor} strokeWidth={2.6} />

          {peoplePoints.map((p, i) => (
            <Circle key={`people-${i}`} cx={p.x} cy={p.y} r={3.2} fill={peopleColor} />
          ))}
          {vehiclesPoints.map((p, i) => (
            <Circle key={`veh-${i}`} cx={p.x} cy={p.y} r={3.2} fill={vehicleColor} />
          ))}

          {activePeople ?
            <Circle cx={activePeople.x} cy={activePeople.y} r={6} fill="none" stroke={peopleColor} strokeWidth={2} />
          : null}
          {activeVehicles ?
            <Circle
              cx={activeVehicles.x}
              cy={activeVehicles.y}
              r={6}
              fill="none"
              stroke={vehicleColor}
              strokeWidth={2}
            />
          : null}

          {series.labels.map((label, i) => (
            <SvgText
              key={`label-${i}`}
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
              x={xs[i]}
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- false positive: x/y are SVG attributes, not RN View props
              y={X_AXIS_LABEL_Y}
              fontSize={10}
              textAnchor="middle"
              fill={labelColor}
            >
              {label}
            </SvgText>
          ))}
        </Svg>

        {hoverIndex !== null && tooltip ?
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              left: tooltip.left,
              top: tooltip.top,
              width: TOOLTIP_WIDTH,
              backgroundColor: tooltipBg,
              borderColor: tooltipBorder,
              borderWidth: 1,
              borderRadius: 8,
              paddingHorizontal: 12,
              paddingVertical: 8,
            }}
          >
            <Text textStyle="xs/semibold" color="text">
              {series.labels[hoverIndex]} · People {(series.people[hoverIndex] ?? 0).toLocaleString()} · Vehicles{" "}
              {(series.vehicles[hoverIndex] ?? 0).toLocaleString()}
            </Text>
          </View>
        : null}
      </View>

      <View flexDirection="row" gap="md" alignItems="center">
        <LegendKey color={peopleColor} label="People" />
        <LegendKey color={vehicleColor} label="Vehicles" />
      </View>
    </View>
  );
}

interface LegendKeyProps {
  color: string;
  label: string;
}

function LegendKey({ color, label }: LegendKeyProps) {
  return (
    <View flexDirection="row" alignItems="center" gap="xs">
      <View style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: color }} />
      <Text textStyle="xs/regular" color="textMuted">
        {label}
      </Text>
    </View>
  );
}

interface TooltipPlacementInput {
  svgX: number;
  containerWidth: number;
}

interface TooltipPlacement {
  left: number;
  top: number;
}

/**
 * Place the tooltip near the active point in container-pixel space,
 * flipping left/right of the point so it never spills outside the
 * chart. The vertical position is fixed near the top of the plot
 * since both series values fit in a single line.
 */
function buildTooltipPlacement({ svgX, containerWidth }: TooltipPlacementInput): TooltipPlacement {
  const scale = containerWidth / VIEW_W;
  const pointPx = svgX * scale;

  const rightEdgeIfRight = pointPx + TOOLTIP_GAP + TOOLTIP_WIDTH;
  const fitsRight = rightEdgeIfRight <= containerWidth - 4;
  const left = fitsRight ? pointPx + TOOLTIP_GAP : Math.max(4, pointPx - TOOLTIP_GAP - TOOLTIP_WIDTH);

  return { left, top: 8 };
}

interface PlotPoint {
  x: number;
  y: number;
}

interface PlotData {
  peoplePath: string;
  vehiclesPath: string;
  peoplePoints: readonly PlotPoint[];
  vehiclesPoints: readonly PlotPoint[];
  gridYs: readonly number[];
  /**
   * Y-axis tick values aligned with `gridYs`. Index 0 is the top
   * gridline (= `vmax`); the last entry is always 0 (= the baseline).
   * Rounded to integers because every series here is a count.
   */
  gridValues: readonly number[];
  xs: readonly number[];
}

function buildPlot(series: DailyActivitySeries): PlotData {
  const innerW = VIEW_W - PAD_X_LEFT - PAD_X_RIGHT;
  const innerH = VIEW_H - PAD_Y_TOP - PAD_Y_BOTTOM;
  const n = series.labels.length;
  const stepX = n > 1 ? innerW / (n - 1) : 0;

  const xs: number[] = [];
  for (let i = 0; i < n; i += 1) xs.push(PAD_X_LEFT + stepX * i);

  const yFor = (v: number): number => {
    const ratio = series.vmax > 0 ? v / series.vmax : 0;
    return PAD_Y_TOP + innerH * (1 - ratio);
  };

  const peoplePoints: PlotPoint[] = series.people.map((v, i) => ({ x: xs[i] ?? 0, y: yFor(v) }));
  const vehiclesPoints: PlotPoint[] = series.vehicles.map((v, i) => ({ x: xs[i] ?? 0, y: yFor(v) }));

  const gridYs: number[] = [];
  const gridValues: number[] = [];
  /**
   * Top-down sweep: index 0 sits at `vmax`, index `GRID_LINES - 1`
   * at zero. We round to integers because every chart series here
   * is a count — fractional ticks would only confuse the reader.
   * The last entry is forced to 0 to defend against rounding drift
   * at the baseline.
   */
  for (let i = 0; i < GRID_LINES; i += 1) {
    const ratio = i / (GRID_LINES - 1);
    gridYs.push(PAD_Y_TOP + innerH * ratio);
    gridValues.push(i === GRID_LINES - 1 ? 0 : Math.round(series.vmax * (1 - ratio)));
  }

  return {
    peoplePath: linearPath(peoplePoints),
    vehiclesPath: linearPath(vehiclesPoints),
    peoplePoints,
    vehiclesPoints,
    gridYs,
    gridValues,
    xs,
  };
}

/**
 * Build a straight polyline path through `points` — `M` to the first
 * point, `L` to each subsequent point.
 *
 * We previously smoothed with a Catmull-Rom-style cubic, but the
 * curve overshoots the data range when one weekday's value is much
 * higher than its neighbours (e.g. a Thursday vehicle spike pulled
 * the curve below the X-axis on the way back down to Fri). A
 * polyline can't overshoot — its extrema are always sample points —
 * which is the property we want here. The data is also sparse (one
 * sample per weekday), so smoothing was hiding the discrete nature
 * of the series rather than revealing trend.
 */
function linearPath(points: readonly PlotPoint[]): string {
  if (points.length === 0) return "";
  let d = `M ${points[0]!.x.toFixed(2)} ${points[0]!.y.toFixed(2)}`;
  for (let i = 1; i < points.length; i += 1) {
    const p = points[i]!;
    d += ` L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
  }
  return d;
}
